import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Stack, Link
} from '@mui/material';
import * as api from './currenciesApi';
import type { Currency } from './currenciesApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell } from '../common/detailView';

interface CurrencyViewProps {
    currencyId: number | null;
}

export function CurrencyView({ currencyId }: CurrencyViewProps) {
    const { t } = useTranslation();
    const detail = useDetailView<Currency>();

    const loadData = useCallback(async () => {
        await detail.withLoading(async () => {
            if (currencyId === null) {
                const empty: Currency = {
                    id: 0,
                    code: '',
                    name: '',
                    number: '',
                    currencyRate: 0,
                };
                detail.setItem(empty);
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getCurrency(currencyId);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [currencyId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const hasCode = (): boolean => !!detail.item?.code && detail.item.code.length > 0;
    const hasName = (): boolean => !!detail.item?.name && detail.item.name.length > 0;
    const hasNumber = (): boolean => !!detail.item?.number && detail.item.number.length === 3;
    const hasRate = (): boolean => detail.item?.currencyRate != null && !isNaN(Number(detail.item.currencyRate));
    const isAllValid = (): boolean => hasCode() && hasName() && hasNumber() && hasRate();

    const save = async () => {
        if (!detail.item) return;
        await detail.withSaving(async () => {
            if (detail.isNew) {
                const response = await api.saveCurrency(detail.item!);
                detail.setItem(response);
                detail.setIsNew(false);
            } else {
                const response = await api.updateCurrency(detail.item!);
                detail.setItem(response);
            }
            detail.setIsEditing(false);
        });
    };

    const deleteCurrency = async () => {
        if (!detail.item) return;
        if (!window.confirm(t('currency.confirmDelete'))) return;
        await detail.withSaving(async () => {
            await api.deleteCurrency(detail.item!.id);
            window.history.back();
        });
    };

    return (
        <DetailViewShell<Currency>
            title={`${detail.item?.code ?? ''} ${detail.item?.name ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isAllValid()}
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={deleteCurrency}
            onClearError={() => detail.setError(null)}
        >
            {(currency) => (<>
                <FormRow label={t('currency.code')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>{currency.code}</Typography>
                    ) : (
                        <TextField size="small" fullWidth value={currency.code}
                            onChange={e => detail.update('code', e.target.value)}
                            error={!hasCode()} />
                    )}
                </FormRow>

                <FormRow label={t('currency.name')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>{currency.name}</Typography>
                    ) : (
                        <TextField size="small" fullWidth value={currency.name}
                            onChange={e => detail.update('name', e.target.value)}
                            error={!hasName()} />
                    )}
                </FormRow>

                <FormRow label={t('currency.number')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>{currency.number}</Typography>
                    ) : (
                        <TextField size="small" fullWidth placeholder="000"
                            inputProps={{ maxLength: 3 }}
                            value={currency.number}
                            onChange={e => detail.update('number', e.target.value.replace(/[^0-9]/g, ''))}
                            error={!hasNumber()}
                            helperText={!hasNumber() ? `${t('common.required')} 3${t('common.piecesAbbr')}` : undefined} />
                    )}
                </FormRow>

                <FormRow label={t('currency.currencyRate')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>{currency.currencyRate}</Typography>
                    ) : (
                        <TextField size="small" type="number" fullWidth
                            inputProps={{ step: 'any' }}
                            value={currency.currencyRate ?? ''}
                            onChange={e => detail.update('currencyRate', e.target.value === '' ? 0 : Number(e.target.value))}
                            error={!hasRate()} />
                    )}
                </FormRow>

                <Box pt={2}>
                    <Link href="https://sv.wikipedia.org/wiki/ISO_4217" target="_blank" rel="noreferrer" sx={{ fontSize: '16px', color: '#337ab7', textDecoration: 'underline' }}>
                        {t('role.currency')} <span style={{ textTransform: 'lowercase' }}>{t('currency.code')}/{t('currency.number')} {t('common.information')}:</span> ISO 4217
                    </Link>
                </Box>
            </>)}
        </DetailViewShell>
    );
}
